import React, { useState, useRef, useEffect } from 'react';
import { NodeViewWrapper } from '@tiptap/react';

const labelSt = {
  display: 'flex', flexDirection: 'column', gap: 3,
  fontSize: 11, color: '#555', fontWeight: 500, marginBottom: 8,
};
const fieldSt = {
  padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 4,
  fontSize: 12, width: '100%', boxSizing: 'border-box',
};

const CAPTION_SIZES = [
  { label: 'S', value: 11 },
  { label: 'M', value: 13 },
  { label: 'L', value: 15 },
];

// Thin separator for the mini-toolbar
const TS = () => (
  <span style={{ width: 1, height: 14, background: '#4b5563', margin: '0 2px', display: 'inline-block', verticalAlign: 'middle' }} />
);

export default function TestEditorImageView({ node, updateAttributes, selected, deleteNode, getPos, editor }) {
  const {
    src, alt, title, width,
    alignment = 'center', caption = '', indent = 0,
    captionBold = false, captionItalic = true,
    captionSize = 13, captionColor = '#555555',
    captionAlign = 'left', captionMargin = 6,
  } = node.attrs;

  const imgRef = useRef(null);
  const [hovered,        setHovered]        = useState(false);
  const [showEdit,       setShowEdit]       = useState(false);
  const [editAlt,        setEditAlt]        = useState(alt   || '');
  const [editTitle,      setEditTitle]      = useState(title || '');
  const [editWidth,      setEditWidth]      = useState(width != null ? String(width) : '');
  const [editAlign,      setEditAlign]      = useState(alignment || 'center');
  const [editCaption,    setEditCaption]    = useState(caption || '');
  const [editingCaption, setEditingCaption] = useState(false);
  const [captionHovered, setCaptionHovered] = useState(false);

  useEffect(() => {
    setEditAlt(alt   || '');
    setEditTitle(title || '');
    setEditWidth(width != null ? String(width) : '');
    setEditAlign(alignment || 'center');
    setEditCaption(caption || '');
  }, [alt, title, width, alignment, caption]);

  const saveEdit = () => {
    updateAttributes({
      alt:       editAlt.trim(),
      title:     editTitle.trim(),
      width:     editWidth !== '' ? Number(editWidth) : null,
      alignment: editAlign,
    });
    setShowEdit(false);
  };

  // Bottom-right corner — drag to resize
  const handleResizeStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const img = imgRef.current;
    if (!img) return;
    const startX     = e.clientX;
    const startWidth = img.getBoundingClientRect().width;
    const onMove = (ev) => {
      const newW = Math.round(Math.max(50, startWidth + (ev.clientX - startX)));
      updateAttributes({ width: newW });
      setEditWidth(String(newW));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup',   onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup',   onUp);
  };

  // Left-edge handle — drag to adjust horizontal indent
  const handleIndentStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX      = e.clientX;
    const startIndent = indent || 0;
    const onMove = (ev) => {
      const newIndent = Math.max(0, Math.min(600, startIndent + (ev.clientX - startX)));
      updateAttributes({ indent: Math.round(newIndent), alignment: 'none' });
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup',   onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup',   onUp);
  };

  const setAlignment = (al) => {
    updateAttributes({ alignment: al, indent: 0 });
    setEditAlign(al);
  };

  const adjustMargin = (delta) => {
    updateAttributes({ captionMargin: Math.max(0, Math.min(40, (captionMargin || 6) + delta)) });
  };

  const textAlign      = alignment === 'center' ? 'center' : alignment === 'right' ? 'right' : 'left';
  const supportsIndent = alignment === 'none' || alignment === 'left';
  const active         = hovered || selected;

  const imgStyle = {
    display:    'block',
    width:      width != null ? `${width}px` : '100%',
    maxWidth:   '100%',
    height:     'auto',
    borderRadius: 4,
    border:     active ? '2px solid #2563eb' : '2px solid transparent',
    cursor:     'default',
    boxSizing:  'border-box',
    userSelect: 'none',
  };

  const captionTextStyle = {
    fontWeight: captionBold   ? 700 : 400,
    fontStyle:  captionItalic ? 'italic' : 'normal',
    fontSize:   captionSize,
    color:      caption ? captionColor : '#aaa',
  };

  const showCaptionArea = caption || active;

  return (
    <NodeViewWrapper style={{
      textAlign,
      margin:      '0.8em 0',
      display:     'block',
      paddingLeft: supportsIndent && indent ? `${indent}px` : undefined,
    }}>
      {/*
        Outer inline-block — width matches the image.
        Caption lives inside here so its textAlign is relative to the image edges,
        not the page. Resize handle is on the image's own relative div.
      */}
      <div
        style={{ display: 'inline-block', maxWidth: '100%', verticalAlign: 'top' }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >

        {/* ── Image wrapper — position:relative anchors toolbar + resize handle ── */}
        <div style={{ position: 'relative' }}>

          {/* Left-edge indent handle */}
          {active && (
            <div
              onMouseDown={handleIndentStart}
              title="Drag left/right to adjust horizontal position"
              style={{
                position: 'absolute', left: 0, top: '50%',
                transform: 'translateY(-50%)',
                width: 14, height: 36,
                background: '#2563eb', borderRadius: '0 4px 4px 0',
                cursor: 'ew-resize', zIndex: 20,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontSize: 11, userSelect: 'none',
              }}
            >↔</div>
          )}

          <img
            ref={imgRef}
            src={src}
            alt={alt || ''}
            title={title || ''}
            style={imgStyle}
            draggable={false}
            onMouseDown={(e) => {
              if (e.button !== 0) return;
              e.preventDefault();
              if (typeof getPos === 'function' && editor) {
                editor.commands.setNodeSelection(getPos());
              }
            }}
          />

          {/* Selection toolbar */}
          {active && (
            <div style={{
              position: 'absolute', top: -36, left: 0,
              display: 'flex', alignItems: 'center', gap: 4, zIndex: 30,
              background: '#13273F', borderRadius: '5px 5px 0 0',
              padding: '3px 7px', whiteSpace: 'nowrap',
            }}>
              {!alt ? (
                <span style={{ fontSize: 11, color: '#fbbf24', fontWeight: 700, marginRight: 2 }}>⚠ No alt text</span>
              ) : (
                <span style={{ fontSize: 11, color: '#86efac' }}>✓ {alt.length > 18 ? alt.slice(0, 18) + '…' : alt}</span>
              )}
              {[['left','⬅'],['center','☰'],['right','➡']].map(([al, icon]) => (
                <button
                  key={al}
                  onMouseDown={e => { e.preventDefault(); setAlignment(al); }}
                  title={`Align ${al}`}
                  style={{
                    fontSize: 11, padding: '2px 5px',
                    background: alignment === al ? '#2563eb' : '#334',
                    color: '#fff', border: 'none', borderRadius: 3, cursor: 'pointer',
                  }}
                >{icon}</button>
              ))}
              <button
                onMouseDown={e => { e.preventDefault(); setShowEdit(v => !v); }}
                style={{ fontSize: 11, padding: '2px 8px', background: showEdit ? '#2563eb' : '#334', color: '#fff', border: 'none', borderRadius: 3, cursor: 'pointer' }}
              >Edit</button>
              <button
                onMouseDown={e => { e.preventDefault(); if (window.confirm('Remove this image?')) deleteNode(); }}
                style={{ fontSize: 11, padding: '2px 6px', background: '#7f1d1d', color: '#fff', border: 'none', borderRadius: 3, cursor: 'pointer' }}
              >✕</button>
            </div>
          )}

          {/* Resize handle — bottom-right of image, not caption */}
          {active && (
            <div
              onMouseDown={handleResizeStart}
              title="Drag to resize"
              style={{
                position: 'absolute', right: -1, bottom: -1,
                width: 13, height: 13,
                background: '#2563eb', borderRadius: '50%',
                cursor: 'nw-resize', zIndex: 30,
                border: '2px solid #fff', boxSizing: 'border-box',
              }}
            />
          )}

          {/* Edit panel */}
          {showEdit && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 40,
              background: '#fff', border: '1px solid #d1d5db', borderRadius: 7,
              padding: 14, boxShadow: '0 6px 24px rgba(0,0,0,.16)', width: 320,
              boxSizing: 'border-box',
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#13273F', marginBottom: 10 }}>Edit Image</div>

              <label style={labelSt}>
                Alt text — ADA / screen reader description
                <input
                  value={editAlt}
                  onChange={e => setEditAlt(e.target.value)}
                  placeholder="e.g. A photo of the city hall building"
                  style={fieldSt}
                  autoFocus
                />
                {!editAlt.trim() && (
                  <span style={{ fontSize: 10, color: '#ef4444', fontWeight: 600 }}>Required for accessibility (WCAG 2.1)</span>
                )}
              </label>

              <label style={labelSt}>
                Title (shown as tooltip on hover)
                <input
                  value={editTitle}
                  onChange={e => setEditTitle(e.target.value)}
                  placeholder="Optional"
                  style={fieldSt}
                />
              </label>

              <div style={{ display: 'flex', gap: 8 }}>
                <label style={{ ...labelSt, flex: 1 }}>
                  Width (px)
                  <input
                    type="number" min={50} max={2000}
                    value={editWidth}
                    onChange={e => setEditWidth(e.target.value)}
                    placeholder="Leave blank = 100%"
                    style={fieldSt}
                  />
                </label>
                <label style={{ ...labelSt, flex: 1 }}>
                  Alignment
                  <select value={editAlign} onChange={e => setEditAlign(e.target.value)} style={fieldSt}>
                    <option value="none">None (left)</option>
                    <option value="left">Left</option>
                    <option value="center">Center</option>
                    <option value="right">Right</option>
                  </select>
                </label>
              </div>

              <div style={{ fontSize: 10, color: '#888', marginTop: -6, marginBottom: 8 }}>
                Drag the ↔ handle to nudge position · drag the corner circle to resize.
              </div>

              <div style={{ display: 'flex', gap: 7, marginTop: 2 }}>
                <button
                  onMouseDown={e => { e.preventDefault(); saveEdit(); }}
                  style={{ flex: 1, padding: '6px 0', background: '#13273F', color: '#fff', border: 'none', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                >Save Changes</button>
                <button
                  onMouseDown={e => { e.preventDefault(); setShowEdit(false); }}
                  style={{ padding: '6px 12px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}
                >Cancel</button>
              </div>
            </div>
          )}

        </div>
        {/* ── end image wrapper ── */}

        {/* ── Caption — inside the inline-block so textAlign is relative to image width ── */}
        {showCaptionArea && (
          <div
            onMouseEnter={() => setCaptionHovered(true)}
            onMouseLeave={() => setCaptionHovered(false)}
            style={{ position: 'relative', marginTop: captionMargin }}
          >

            {/* Caption mini-toolbar */}
            {(captionHovered || editingCaption) && (
              <div style={{
                position: 'absolute', top: -30, left: 0,
                display: 'flex', alignItems: 'center', gap: 2, zIndex: 50,
                background: '#13273F', borderRadius: 4, padding: '3px 6px',
                whiteSpace: 'nowrap',
              }}>

                {/* Alignment */}
                {[['left','⬅'],['center','☰'],['right','➡']].map(([al, icon]) => (
                  <button
                    key={al}
                    onMouseDown={e => { e.preventDefault(); updateAttributes({ captionAlign: al }); }}
                    title={`Caption align ${al}`}
                    style={{
                      fontSize: 11, padding: '1px 5px',
                      background: captionAlign === al ? '#2563eb' : '#334',
                      color: '#fff', border: 'none', borderRadius: 3, cursor: 'pointer',
                    }}
                  >{icon}</button>
                ))}

                <TS />

                {/* Margin */}
                <button
                  onMouseDown={e => { e.preventDefault(); adjustMargin(-2); }}
                  title="Less space above caption"
                  style={{ fontSize: 12, padding: '1px 5px', background: '#334', color: '#fff', border: 'none', borderRadius: 3, cursor: 'pointer' }}
                >−</button>
                <span style={{ fontSize: 10, color: '#9ca3af', minWidth: 28, textAlign: 'center' }}>{captionMargin}px</span>
                <button
                  onMouseDown={e => { e.preventDefault(); adjustMargin(2); }}
                  title="More space above caption"
                  style={{ fontSize: 12, padding: '1px 5px', background: '#334', color: '#fff', border: 'none', borderRadius: 3, cursor: 'pointer' }}
                >+</button>

                <TS />

                {/* Bold */}
                <button
                  onMouseDown={e => { e.preventDefault(); updateAttributes({ captionBold: !captionBold }); }}
                  title="Bold"
                  style={{ fontSize: 12, fontWeight: 700, padding: '1px 6px', background: captionBold ? '#2563eb' : '#334', color: '#fff', border: 'none', borderRadius: 3, cursor: 'pointer' }}
                >B</button>

                {/* Italic */}
                <button
                  onMouseDown={e => { e.preventDefault(); updateAttributes({ captionItalic: !captionItalic }); }}
                  title="Italic"
                  style={{ fontSize: 12, fontStyle: 'italic', padding: '1px 6px', background: captionItalic ? '#2563eb' : '#334', color: '#fff', border: 'none', borderRadius: 3, cursor: 'pointer' }}
                >I</button>

                <TS />

                {/* Size */}
                {CAPTION_SIZES.map(s => (
                  <button
                    key={s.value}
                    onMouseDown={e => { e.preventDefault(); updateAttributes({ captionSize: s.value }); }}
                    title={`Size ${s.label}`}
                    style={{ fontSize: 10, padding: '1px 5px', background: captionSize === s.value ? '#2563eb' : '#334', color: '#fff', border: 'none', borderRadius: 3, cursor: 'pointer' }}
                  >{s.label}</button>
                ))}

                <TS />

                {/* Color */}
                <label title="Caption color" style={{ display: 'inline-flex', alignItems: 'center', gap: 2, cursor: 'pointer', padding: '1px 3px' }}>
                  <span style={{ fontSize: 11, color: '#fff' }}>A</span>
                  <input
                    type="color"
                    value={captionColor}
                    onMouseDown={e => e.stopPropagation()}
                    onChange={e => updateAttributes({ captionColor: e.target.value })}
                    style={{ width: 14, height: 14, padding: 0, border: 'none', cursor: 'pointer', borderRadius: 2, background: 'none' }}
                  />
                </label>

              </div>
            )}

            {/* Caption text or input */}
            {editingCaption ? (
              <input
                autoFocus
                value={editCaption}
                onChange={e => setEditCaption(e.target.value)}
                onBlur={() => { updateAttributes({ caption: editCaption.trim() }); setEditingCaption(false); }}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === 'Escape') {
                    updateAttributes({ caption: editCaption.trim() });
                    setEditingCaption(false);
                  }
                }}
                placeholder="Add a caption…"
                style={{
                  display: 'block', textAlign: captionAlign, width: '100%',
                  border: 'none', borderBottom: '1px dashed #9ca3af',
                  background: 'transparent', padding: '2px 0', outline: 'none',
                  boxSizing: 'border-box',
                  ...captionTextStyle,
                  color: captionColor,
                }}
              />
            ) : (
              <div
                onMouseDown={e => { e.preventDefault(); setEditingCaption(true); }}
                style={{
                  textAlign: captionAlign,
                  cursor: 'text', minHeight: 18, padding: '2px 0',
                  borderBottom: (captionHovered || active) ? '1px dashed #d1d5db' : '1px solid transparent',
                  ...captionTextStyle,
                }}
              >
                {caption || ((captionHovered || active) ? 'Add a caption…' : '')}
              </div>
            )}

          </div>
        )}

      </div>
    </NodeViewWrapper>
  );
}
